import { AnimatePresence, motion } from "framer-motion";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { Button, Collapse, Dropdown } from "react-bootstrap";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaFileCsv,
  FaFileExport,
  FaListUl,
  FaSpinner,
  FaTimesCircle,
} from "react-icons/fa";
import { IoRefresh } from "react-icons/io5";
import { LuLogs } from "react-icons/lu";
import { getMomentObj } from "../../Utilities/Utilities.ts";
import TooltipButton from "../Tools/TooltipButton.tsx";
import { DistributionLogModel, ReportModel } from "./_core/_model.ts";
import { GetReportDistributionLogs } from "./_core/_request.ts";
import { ThemeContext } from "../../context/ThemeContext.js";

interface Props {
  reportDistributionID: number,
  report: ReportModel,
  onBack: () => void;
}

const DistributionLogViewer: React.FC<Props> = ({ reportDistributionID, report, onBack }) => {
  const [filter, setFilter] = useState<"all" | "success" | "fail">("all");
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const { themeConfig } = useContext(ThemeContext);

  const [logs, setLogs] = useState<DistributionLogModel[] | null>(null);

  const initLogs = () => {
    setLogs(null);
    GetReportDistributionLogs(reportDistributionID).then((x) => {
      setLogs([...x.data]);
    }, () => {
      setLogs([]);
    });
  }
  useEffect(() => {
    initLogs();
  }, [report])

  const filteredLogs =
    filter === "all" ? logs : logs?.filter((log) => log.status === filter);

  const handleExport = (type: "csv" | "json") => {
    const filename = `distribution_logs.${type}`;
    if (filteredLogs != null) {
      const content =
        type === "json"
          ? JSON.stringify(filteredLogs, null, 2)
          : ["Status,Timestamp,Message", ...filteredLogs.map(
            (l) => `${l.status},${l.timestamp},"${l.message.replace(/"/g, '"')}"`
          )].join("\n");

      const blob = new Blob([content], {
        type: type === "json" ? "application/json" : "text/csv",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const groupLogsByDate = (logs: DistributionLogModel[] | null | undefined) => {
    if (typeof logs == 'undefined' || logs == null)
      return [];
    const groups: Record<string, DistributionLogModel[]> = {};
    logs.forEach((log) => {
      const date = getMomentObj(log.timestamp).local();
      const key = date.startOf("day").format("YYYY-MM-DD");
      if (!groups[key]) groups[key] = [];
      groups[key].push(log);
    });
    return Object.entries(groups).sort(([a], [b]) => (a > b ? -1 : 1));
  };

  const getRelativeLabel = (dateStr: string) => {
    const date = getMomentObj(dateStr);
    const today = moment().startOf("day");
    const diff = today.diff(date.startOf("day"), "days");
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    return `${diff} days ago`;
  };

  return (
    <motion.div
      key="log-view"
      initial={{ x: 30, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -30, opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
        <div className="d-flex align-items-center gap-2">
          <Button variant="light" size="sm" onClick={onBack}>
            <FaArrowLeft />
          </Button>
          <h5 className="mb-0 d-flex align-items-center gap-2 text-primary">
            <FaListUl /> Logs - {`Historic Log for ${report.reportName}`}
          </h5>
        </div>
        <TooltipButton title="Refresh">
          <button className="btn btn-outline-primary p-0" onClick={initLogs}>
            <IoRefresh size={24} />
          </button>
        </TooltipButton>
      </div>

      {/* Controls */}
      <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom bg-card">
        <div className="d-flex flex-row align-items-center gap-0">
          <label className="me-2">Filter:</label>
          <Dropdown onSelect={(k) => setFilter(k as any)}>
            <Dropdown.Toggle variant="outline-secondary" size="sm">
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item eventKey="all">All</Dropdown.Item>
              <Dropdown.Item eventKey="success">Success</Dropdown.Item>
              <Dropdown.Item eventKey="fail">Fail</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div>
          <label className="me-2">Export:</label>
          <Button
            variant={(logs == null || logs.length == 0) ? "outline-secondary" : "outline-success"}
            disabled={logs == null || logs.length == 0}
            size="sm"
            className="me-2"
            onClick={() => handleExport("csv")}
          >
            <FaFileCsv className="me-1" /> CSV
          </Button>
          <Button
            variant={(logs == null || logs.length == 0) ? "outline-secondary" : "outline-success"}
            disabled={logs == null || logs.length == 0}
            size="sm"
            onClick={() => handleExport("json")}
          >
            <FaFileExport className="me-1" /> JSON
          </Button>
        </div>
      </div>

      {/* Logs */}
      <div className="p-4 overflow-auto" style={{ height: "calc(100vh - 290px)" }}>
        {logs == null ?
          <div className="d-flex flex-column align-items-center text-success mt-4">
            <AnimatePresence>
              <motion.div
                animate={{ rotate: 360 }}
                style={{
                  display: 'inline-block',
                  width: '40px',
                  height: '40px',
                  marginBottom: '10px'
                }}
                transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
              >
                <FaSpinner className="fa-spin mb-2" size={32} style={{
                  fontSize: '2.5rem',
                  color: '#198754',
                  width: '100%',
                  height: '100%'
                }} />
              </motion.div>
            </AnimatePresence>
            <span>Loading Logs...</span>
          </div> :
          filteredLogs?.length === 0 ? (
            <AnimatePresence>
              <motion.div
                className="d-flex flex-column align-items-center justify-content-center text-muted-theme px-5 py-2 w-100"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                style={{
                  border: "2px dashed #dee2e6",
                  borderRadius: "10px",
                  marginTop: "40px",
                  backgroundColor: themeConfig.colors.background,
                }}
              >
                <LuLogs size={60} color="#adb5bd" className="mb-3" />
                <h5 className="fw-semibold">No Logs</h5>
                <p className="text-center" style={{ maxWidth: 400 }}>
                  It seems this distribution has not run yet.
                </p>
              </motion.div>
            </AnimatePresence>
          ) : (
            groupLogsByDate(filteredLogs).map(([dateStr, entries], groupIdx) => (
              <div key={groupIdx} className="mb-4">
                <h6 className="text-muted-theme border-bottom pb-1 mb-3">
                  {getMomentObj(dateStr).local().format("MMMM YYYY")} • {getRelativeLabel(dateStr)}
                </h6>
                {entries.map((log, i) => {
                  const localTime = getMomentObj(log.timestamp).local();
                  const isExpanded = expandedIndex === log.timestamp.length + i;
                  return (
                    <div key={i} className="border rounded mb-2 p-3 bg-light">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="d-flex align-items-start gap-3">
                          <div className="pt-1">
                            {log.status === "success" ? (
                              <FaCheckCircle className="text-success" size={20} />
                            ) : (
                              <FaTimesCircle className="text-danger" size={20} />
                            )}
                          </div>
                          <div>
                            <div className="fw-semibold">{log.message.slice(0, 50)}...</div>
                            <div className="text-muted-theme small">{localTime.format("h:mm A • D MMM, YYYY")}</div>
                          </div>
                        </div>
                        <Button
                          variant="link"
                          className="text-decoration-none p-0"
                          onClick={() => setExpandedIndex(isExpanded ? null : log.timestamp.length + i)}
                        >
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </Button>
                      </div>
                      <Collapse in={isExpanded}>
                        <div className="mt-3 text-muted-theme small">
                          {log.description}
                        </div>
                      </Collapse>
                    </div>
                  );
                })}
              </div>
            ))
          )}
      </div>
    </motion.div>
  );
};

export default DistributionLogViewer;